/* eslint-disable @typescript-eslint/no-var-requires */
// react
import React, { useEffect, useRef, useState } from "react";
// mui
import { Button } from "@mui/material";
// style
import styles from "./UnityCanvas.module.scss";
// hooks
import { useUnity } from "../hooks/useUnity";
import { useConnect } from "../hooks/useConnect";
// components
// function
import { socket } from "../util/SocketProvider";
import { SkyWayClient, type SkyWayDataStreamMessageType } from "../util/SkyWayProvider";
// external json
import unityBinaryData from "../unityBinaryData.json";
import type { ConnectionPublisher, DataChannelEvent, DataChannelMessageEvent } from "sora-js-sdk";
// logger
import log from "../util/logger";

import { RequestUserTokenResponseJson } from "../App";

const HelpPageURL = "https://prd.xrccg.com:4004/faq";

type Props = {
    userTokenJson: RequestUserTokenResponseJson | null;
};

type SendUserTokenType = {
    token: string;
};

type SendRtcSettingsType = {
    settings: string;
};

type SendVoiceRecognitionType = {
    type: string;
};

export type RtcSettings = {
    service_type: string;
    audio_code_type: string;
    video_code_type: string;
    video_resolution_width: number;
    video_resolution_height: number;
    video_fps_max: number;
    video_fps_min: number;
};

type NotificationMessage = {
    type: string;
    method: string;
    peer_id: string;
    from_id: string;
};

type ResponseMessage = {
    method: string;
    result: string;
    message: string;
    sora_access_token: SoraAccessToken;
    channel_id: string;
};

type SoraAccessToken = {
    access_token: string;
};

type SetVerticalLayoutFlagType = {
    verticalFlag: boolean;
};

type StartListeningType = {
    language: string;
    continuous: boolean;
};

type LoginAccessGroup = {
    group: string;
    history: string;
};

type BrowserSize = {
    width: number;
    height: number;
};

type CallParameter = {
    parameter: string;
};

const unityBuildRoot = "./Build";
export let unityInstanceRef: React.MutableRefObject<UnityInstance | undefined>;
let unityContainerRef: React.MutableRefObject<HTMLDivElement | null>;
export let orientationParameter: string | null = null;
let verticalFlag: boolean;

export const resolutions_landscape = [
    { width: 3840, height: 2160 },
    { width: 2560, height: 1440 },
    { width: 1920, height: 1080 },
    { width: 1600, height: 900 },
    { width: 1366, height: 768 },
    { width: 1280, height: 720 },
    { width: 1024, height: 576 },
    { width: 800, height: 450 },
    { width: 640, height: 360 },
    { width: 480, height: 270 },
    { width: 320, height: 180 },
];

export const resolutions_portrait = [
    { width: 2160, height: 3840 },
    { width: 1440, height: 2560 },
    { width: 1080, height: 1920 },
    { width: 900, height: 1600 },
    { width: 768, height: 1366 },
    { width: 720, height: 1280 },
    { width: 576, height: 1024 },
    { width: 450, height: 800 },
    { width: 360, height: 640 },
    { width: 270, height: 480 },
    { width: 180, height: 320 },
];

// キャンバスサイズ調整
export const resizeCanvas = (canvasElement: HTMLCanvasElement | null | undefined, verticalFlag: boolean) => {
    if (canvasElement == null || canvasElement == undefined) return;

    // log("resizeCanvas verticalFlag: ", verticalFlag);

    // 現在のブラウザサイズ
    const browserWidth = window.innerWidth;
    const browserHeight = window.innerHeight;

    // キャンバス下部のボタン表示領域を差し引いたサイズ
    const displayWidth = browserWidth;
    const displayHeight = browserHeight - 50;

    // アスペクト比の設定
    let aspectRatio: number;
    let canvasWidth = 0;
    let canvasHeight = 0;

    // verticalFlag に基づいてアスペクト比を設定
    if (verticalFlag) {
        aspectRatio = 9 / 16;
    } else {
        aspectRatio = 16 / 9; // 16:9
    }

    // 解像度テーブル選択
    const selectedResolutions = verticalFlag ? resolutions_portrait : resolutions_landscape;

    // 1つ下の解像度を見つける
    let selectedResolution = { width: 0, height: 0 };
    const sortedResolutions = selectedResolutions.sort((a, b) => {
        return b.width * b.height - a.width * a.height;
    });
    let findResolution = false;
    for (const resolution of sortedResolutions) {
        if (resolution.width <= displayWidth && resolution.height <= displayHeight) {
            selectedResolution = resolution;
            findResolution = true;
            break;
        }
    }
    if (!findResolution) {
        selectedResolution.width = verticalFlag ? 180 : 320;
        selectedResolution.height = verticalFlag ? 320 : 180;
    }

    // 選ばれた解像度に基づいてcanvasをリサイズ
    canvasWidth = selectedResolution.width;
    canvasHeight = selectedResolution.height;
    canvasElement.style.width = canvasWidth.toString() + "px";
    canvasElement.style.height = canvasHeight.toString() + "px";
    // log(`canvas width = ${canvasElement.style.width}, height = ${canvasElement.style.height}`);
};

let isStartRecognition = false;
let continuous = false;
let finalTranscript = "";
let isNativeSupport = false;

export const UnityCanvas = ({ userTokenJson }: Props) => {
    const [fullscreenBtnEnabled, setFullscreenBtnEnabled] = useState(false);
    const [userToken, setUserToken] = useState("");
    const [rtcSettings, setRtcSettings] = useState<RtcSettings>();
    const [voiceRecognitionType, setVoiceRecognitionType] = useState("");
    const [sora, setSora] = useState<ConnectionPublisher>();
    const [skyway, setSkyWay] = useState<SkyWayClient>();
    const [renderCount, setRenderCount] = useState(0);
    const [fullscreenBtnToggle, setFullscreenBtnToggle] = useState(true);
    const [licenseBtnToggle, setLicenseBtnToggle] = useState(true);
    const [helpBtnToggle, setHelpBtnToggle] = useState(false);
    const remoteAudioRef = useRef<HTMLAudioElement>(null);

    // コンポーネント再読み込み
    const renderComponent = () => {
        // 再レンダリング用のステート更新
        log("UnityCanvas 再レンダリング");
        setRenderCount((prevCount) => prevCount + 1);
        // フルスクリーンボタン有効化
        setFullscreenBtnEnabled(false);
    };

    // URLパラメータ解析
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const orientation = params.get("orientation");
    if (orientation != null) {
        // orientationパラメータ指定時は画面向きを設定
        if (orientation != "landscape" && orientation != "portrait") {
            orientationParameter = null;
        } else {
            orientationParameter = orientation;
        }
    }

    // URLパラメータ隠ぺい
    const invisible = params.get("invisible");
    if (invisible != null && invisible == "enable") {
        window.history.replaceState({}, document.title, window.location.pathname);
    }

    // キャンバス初期化
    let canvas = window.document.querySelector("canvas");
    if (!canvas) {
        canvas = window.document.createElement("canvas");
    }
    resizeCanvas(canvas, verticalFlag || orientationParameter == "portrait"); // URLパラメータ未指定時はデフォルト横

    // Unityインスタンス生成
    const handleUnityReady = (instance: UnityInstance) => {
        log(`accessToken = ${userTokenJson?.access_token}, isNativeSupport = ${isNativeSupport}`);
        // トークン送信
        instance.SendMessage("GameManager", "SetUserToken", JSON.stringify(userTokenJson));
        // ブラウザサポート情報送信
        instance.SendMessage("GameManager", "SetNativeSupport", isNativeSupport.toString());
        // ブラウザサイズ送信
        const browserSize: BrowserSize = {
            width: window.innerWidth,
            height: window.innerHeight,
        };
        instance.SendMessage("GameManager", "GetBrowserSize", JSON.stringify(browserSize));
    };
    const { instanceRef, containerRef } = useUnity({
        canvas,
        unityBuildRoot,
        renderCount,
        onComplete: handleUnityReady,
    });
    unityInstanceRef = instanceRef;
    unityContainerRef = containerRef;

    // AmazonConnect
    const { startWebRTCContact, endWebRTCContact, isCalling } = useConnect();

    // Unityスクリプト読み込み
    useEffect(() => {
        // URLパラメータ解析
        const id = params.get("id");
        const fullscreen = params.get("fullscreen");
        const license = params.get("license");
        const help = params.get("help");
        if (id == null) {
            // idパラメータ未指定時=ログイン画面ではフルスクリーンボタン有効化
            setFullscreenBtnEnabled(true);
        }
        if (fullscreen != null && fullscreen == "disable") {
            // fullscreenパラメータでdisable指定時はフルスクリーンボタン非表示
            setFullscreenBtnToggle(false);
        }
        if (license != null && license == "disable") {
            // licenseパラメータでdisable指定時はライセンスボタン非表示
            setLicenseBtnToggle(false);
        }
        if (help != null && help == "enable") {
            // helpパラメータ指定時はヘルプボタン表示
            setHelpBtnToggle(true);
        }

        // ユーザートークンセット
        if (userTokenJson != null) {
            setUserToken(userTokenJson.access_token);
        }

        // Unityスクリプト読み込み
        const scriptSrc = `${unityBuildRoot}/${unityBinaryData.loaderUrl}`;
        const root = window.document.getElementById("root");
        const scriptTag = window.document.createElement("script");
        scriptTag.type = "text/javascript";
        scriptTag.src = scriptSrc;
        if (root) {
            root.appendChild(scriptTag);
        }

        // Unity初期化完了
        const gameControllerInitializedHandler = () => {
            // フルスクリーンボタン有効化
            setFullscreenBtnEnabled(true);
        };

        // Unity初期化完了イベント購読
        window.addEventListener("gamecontroller_initialized", gameControllerInitializedHandler);

        // 外線通話開始
        const contact = (ev: CustomEvent<CallParameter>) => {
            log("contact: begin startWebRTCContact");
            startWebRTCContact("contact", ev.detail.parameter, unityInstanceRef.current);
        };

        // 外線通話切断
        const hangUp = () => {
            log("hangUp: begin endWebRTCContact");
            endWebRTCContact();
        };

        // 外線通話開始イベント
        window.addEventListener("call", contact as EventListenerOrEventListenerObject);
        // 外線通話切断イベント
        window.addEventListener("hang_up", hangUp);

        return () => {
            // Unity初期化完了イベント購読解除
            window.removeEventListener("gamecontroller_initialized", gameControllerInitializedHandler);
            // 外線通話開始イベント
            window.removeEventListener("call", contact as EventListenerOrEventListenerObject);
            // 外線通話切断イベント
            window.removeEventListener("hang_up", hangUp);
        };
    }, [renderCount]);

    // 音声認識タイプ変更時
    useEffect(() => {
        // モバイルチェック
        const mobileAndTabletCheck = () => {
            const userAgent = navigator.userAgent.toLowerCase();
            console.log(`userAgent = ${userAgent}`);
            const isMobile = /iphone|ipod|android.*mobile|windows phone/.test(userAgent);
            const isTablet = /ipad|android(?!.*mobile)/.test(userAgent);
            return isMobile || isTablet;
        };
        const isMobileOrTablet = mobileAndTabletCheck();
        console.log(`Mobile or Tablet: ${isMobileOrTablet}`);

        // Edgeチェック
        const edgeCheck = () => {
            const userAgent = navigator.userAgent.toLowerCase();
            return userAgent.includes("edg");
        };
        const isEdge = edgeCheck();
        console.log(`Edge: ${isEdge}`);

        // 音声認識初期化
        const speechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
        if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
            // ブラウザ対応
            console.log("Web Speech API対応ブラウザです");
            isNativeSupport = true;
        } else {
            // ブラウザ非対応
            console.error("Web Speech API非対応ブラウザです");
            isNativeSupport = false;
        }

        let recognition: SpeechRecognition | null = null;
        if (isNativeSupport && voiceRecognitionType == "native") {
            recognition = new speechRecognition();
            recognition.onresult = (event) => {
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const results = event.results;
                    const transcript = event.results[i][0].transcript;
                    const confidence = event.results[i][0].confidence;
                    if (results[i].isFinal && !isMobileOrTablet && isEdge) {
                        // PC版Edgeのみconfidenceが常に0なので専用分岐処理
                        if (transcript.includes(finalTranscript)) {
                            finalTranscript = transcript;
                        } else if (!finalTranscript.includes(transcript)) {
                            finalTranscript += transcript;
                        }
                        log(`edge音声認識完了: transcript = ${transcript}, finalTranscript = ${finalTranscript}, confidence = ${confidence}`);
                        // 音声認識完了時の最終文字列をUnity側に送信
                        if (unityInstanceRef.current !== undefined) {
                            unityInstanceRef.current.SendMessage("GameManager", "SetSpeakingTranscriptText", finalTranscript);
                        }
                    } else if (results[i].isFinal && confidence !== 0) {
                        // 音声認識文字列を保持
                        finalTranscript += transcript;
                        log(`音声認識完了: transcript = ${transcript}, finalTranscript = ${finalTranscript}, confidence = ${confidence}`);
                        // 音声認識完了時の最終文字列をUnity側に送信
                        if (unityInstanceRef.current !== undefined) {
                            unityInstanceRef.current.SendMessage("GameManager", "SetSpeakingTranscriptText", finalTranscript);
                        }
                    } else {
                        log(`音声認識中: transcript = ${transcript}`);
                        // 音声認識中の経過文字列をUnity側に送信
                        if (unityInstanceRef.current !== undefined) {
                            unityInstanceRef.current.SendMessage("GameManager", "SetSpeakingTranscriptText", transcript);
                        }
                    }
                }
            };
            recognition.onend = () => {
                log(`onend: finalTranscript = ${finalTranscript}, isStartRecognition = ${isStartRecognition}`);
                if (finalTranscript == "" && !continuous) {
                    // 非連続モードでノイズ検知されてしまった場合はリスタート
                    log("認識不可:音声認識リスタート");
                    recognition?.start();
                    return;
                }
                if (isStartRecognition && continuous && isMobileOrTablet) {
                    // 一部のモバイル端末はcontinuousを設定していても単語区切りで勝手に音声認識が終了してしまうのでリスタート
                    log("モバイル端末自動音声認識終了:音声認識リスタート");
                    recognition?.start();
                    return;
                }
                if (finalTranscript != "") {
                    // 音声認識が明示的に終了されたまたは非連続モードかつ音声認識中の場合は送信
                    if (!isStartRecognition || (!continuous && isStartRecognition)) {
                        // 音声認識完了時の最終文字列をUnity側に送信
                        if (unityInstanceRef.current !== undefined) {
                            const temp = finalTranscript;
                            finalTranscript = "";
                            unityInstanceRef.current.SendMessage("GameManager", "SetCompleteTranscriptText", temp);
                        }
                    }
                }
            };
            recognition.onerror = () => {
                console.error("recognition onerror");
            };
            recognition.onnomatch = () => {
                log("recognition onnomatch");
            };
        }
        // 音声認識開始
        const startSpeechRecognitionHandler = (ev: CustomEvent<StartListeningType>) => {
            if (!isNativeSupport || voiceRecognitionType !== "native" || recognition == null) return;
            log(`startSpeechRecognitionHandler: isStartRecognition = ${isStartRecognition}`);
            if (isStartRecognition) return;
            const languageCode = getLanguageCode(ev.detail.language);
            continuous = ev.detail.continuous;
            log("startListening: language code = ", languageCode, " continuous = ", continuous);
            recognition.lang = languageCode;
            recognition.continuous = continuous;
            recognition.interimResults = true;
            try {
                recognition.start();
            } catch (error) {
                console.error("音声認識エラー:", error);
                setTimeout(() => {
                    startSpeechRecognitionHandler(ev);
                }, 100);
            }
            isStartRecognition = true;
        };

        // 音声認識終了
        const stopSpeechRecognitionHandler = () => {
            if (!isNativeSupport || voiceRecognitionType !== "native" || recognition == null) return;
            if (!isStartRecognition) return;
            isStartRecognition = false;
            recognition.stop();
        };

        const getLanguageCode = (languageStr: string): string => {
            languageStr = languageStr.toLowerCase();
            switch (languageStr) {
                case "japanese":
                    return "ja-JP";
                case "english":
                    return "en-US";
                case "simplifiedchinese":
                case "traditionalchinese":
                    return "zh-CN";
                case "korean":
                    return "ko-KR";
                case "russian":
                    return "ru-RU";
                case "spanish":
                    return "es-ES";
                case "arabic":
                    return "ar-SA";
                case "vietnamese":
                    return "vi-VN";
                case "french":
                    return "fr-FR";
                case "german":
                    return "de-DE";
                case "polish":
                    return "pl-PL";
                case "dutch":
                    return "nl-NL";
                case "hindi":
                    return "hi-IN";
                case "indonesian":
                    return "id-ID";
                case "thai":
                    return "th-TH";
                case "turkish":
                    return "tr-TR";
                case "ukrainian":
                    return "uk-UA";
                case "portuguese":
                    return "pt-PT";
                case "greek":
                    return "el-GR";
                default:
                    return "ja-JP";
            }
        };

        // 音声認識開始イベント購読
        window.addEventListener("speechrecognition_start", startSpeechRecognitionHandler as EventListenerOrEventListenerObject);
        // 音声認識終了イベント購読
        window.addEventListener("speechrecognition_end", stopSpeechRecognitionHandler);

        return () => {
            // 音声認識開始イベント購読解除
            window.removeEventListener("speechrecognition_start", startSpeechRecognitionHandler as EventListenerOrEventListenerObject);
            // 音声認識終了イベント購読解除
            window.removeEventListener("speechrecognition_end", stopSpeechRecognitionHandler);
        };
    }, [voiceRecognitionType]);

    // トークン更新時の処理
    useEffect(() => {
        // シグナリングサーバと接続された
        const onConnect = () => {
            // log("onConnect");
            if (unityInstanceRef.current !== undefined) {
                unityInstanceRef.current.SendMessage("GameManager", "SignalingServerOnConnect");
            }
        };

        // シグナリングサーバから切断された
        const onDisconnect = () => {
            // log("onDisconnect");
            if (unityInstanceRef.current !== undefined) {
                unityInstanceRef.current.SendMessage("GameManager", "SignalingServerOnDisconnect");
            }
        };

        // シグナリングサーバからエラー受信
        const onError = (error: string) => {
            log("onError: ", error);
        };

        // シグナリングサーバから通知受信
        const onNotification = (message: NotificationMessage) => {
            if (message.type != undefined) {
                log(`onNotification: type = ${message.type}`);
            } else if (message.method != undefined) {
                log(`onNotification: method = ${message.method}`);
            }
            switch (message.method) {
                case "getCallingUser": {
                    // チャネルIDとアクセストークンを取得
                    const peerID = message.peer_id;
                    if (peerID == null || peerID == undefined) {
                        console.error("peerID is null or undefined!");
                    } else {
                        const channelID = peerID.split(":")[0];
                        const accessToken = peerID.split(":")[1];
                        log(`channelID = ${channelID}, accessToken = ${accessToken}`);
                        if (rtcSettings?.service_type === "sora") {
                            ConnectSora(channelID, accessToken);
                        } else if (rtcSettings?.service_type === "skyway") {
                            if (unityInstanceRef.current !== undefined) {
                                unityInstanceRef.current.SendMessage("GameManager", "WebRTCConnect");
                            }
                            ConnectSkyWay(channelID, accessToken);
                        }
                    }
                    break;
                }
            }
            if (unityInstanceRef.current !== undefined) {
                unityInstanceRef.current.SendMessage("GameManager", "SignalingServerOnNotification", JSON.stringify(message));
            }
        };

        // シグナリングサーバからレスポンス受信
        const onResponse = async (message: ResponseMessage) => {
            log(`onResponse: method = ${message.method}, result = ${message.result}`);
            // Unityにメッセージ送信
            if (unityInstanceRef.current !== undefined) {
                unityInstanceRef.current.SendMessage("GameObject", "GetBrowserSize", JSON.stringify(message));
            }
        };

        // ユーザートークン受信時ハンドラ
        const receivedTokenHandler = (ev: CustomEvent<SendUserTokenType>) => {
            if (userToken != ev.detail.token) {
                setUserToken(ev.detail.token);
            }
        };

        // RTC設定受信時ハンドラ
        const receivedRtcSettingsHandler = (ev: CustomEvent<SendRtcSettingsType>) => {
            const settings: RtcSettings = JSON.parse(ev.detail.settings);
            log(`receivedRtcSettingsHandler: rtc service type = ${settings.service_type}`);
            setRtcSettings(settings);
        };

        // 音声認識設定受信時ハンドラ
        const receivedVoiceRecognitionTypeHandler = (ev: CustomEvent<SendVoiceRecognitionType>) => {
            setVoiceRecognitionType(ev.detail.type);
        };

        const addSocketEvent = () => {
            socket.on("connect", onConnect);
            socket.on("disconnect", onDisconnect);
            socket.on("error", onError);
            socket.on("notification", onNotification);
            socket.on("response", onResponse);
        };

        const removeSocketEvent = () => {
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("error", onError);
            socket.off("notification", onNotification);
            socket.off("response", onResponse);
        };

        // シグナリングサーバ接続開始時ハンドラ
        const connectHandler = () => {
            // log("connectHandler");
            // socket.io接続
            socket.connect();
        };

        // シグナリングサーバ接続終了時ハンドラ
        const disconnectHandler = () => {
            // log("disconnectHandler");
            // socket.io切断
            socket.disconnect();
        };

        // シグナリングサーバログイン時ハンドラ
        const loginHandler = (ev: CustomEvent<LoginAccessGroup>) => {
            const value = userToken + "," + rtcSettings?.service_type + "," + "calling" + "," + ev.detail.group + "," + ev.detail.history;
            log("loginUser value = ", value);
            socket.emit("loginUser", value);
        };

        const WebRTCDisconnect = async () => {
            if (rtcSettings?.service_type === "sora") {
                if (sora == null || sora == undefined) {
                    log("WebRTCDisconnect error: sora is null or undefined");
                } else {
                    sora?.disconnect();
                    log("WebRTCDisconnect completed: sora has disconnect");
                }
            } else if (rtcSettings?.service_type === "skyway") {
                if (skyway == null || skyway == undefined) {
                    log("WebRTCDisconnect error: skyway is null or undefined");
                } else {
                    skyway?.disconnect();
                    log("WebRTCDisconnect completed: skyway has disconnect");
                }
            }
        };

        const ConnectSora = async (channelID: string, accessToken: string) => {
            log("ConnectSora!!!");
            try {
                // Sora JavaScript SDKをdynamic import
                const { SoraProvider } = await import("../util/SoraProvider");

                // サポートされている制約を取得
                const supports = navigator.mediaDevices.getSupportedConstraints();
                log(
                    `browser support constraints: width = ${supports["width"]}, height = ${supports["height"]}, frameRate = ${supports["frameRate"]}`
                );
                // カメラデバイスからストリームを取得
                const videoSettingLog =
                    rtcSettings?.video_code_type == "non"
                        ? "rtcSettings: video codec type = non"
                        : `rtcSettings: video codec type = ${rtcSettings?.video_code_type}, width = ${rtcSettings?.video_resolution_width}, height = ${rtcSettings?.video_resolution_height}, fps max = ${rtcSettings?.video_fps_max}, min = ${rtcSettings?.video_fps_min}`;
                log(`${videoSettingLog}`);
                //! サーバのビデオオプション実装後に書き換え
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video:
                        rtcSettings?.video_code_type == "non"
                            ? false
                            : {
                                  width: { ideal: rtcSettings?.video_resolution_width },
                                  height: { ideal: rtcSettings?.video_resolution_height },
                                  frameRate: { max: rtcSettings?.video_fps_max, min: rtcSettings?.video_fps_min },
                              },
                });
                // Sora初期化
                const sendrecv = SoraProvider({ channelID: channelID, accessToken: accessToken, rtcSettings });
                // log(`channel id = ${channelID}, access token = ${soraAccessToken}`);
                sendrecv.on("track", (event) => {
                    const stream = event.streams[0];
                    // log("sora track");
                    if (!stream) {
                        log(`on track error!`);
                        return;
                    }
                    if (event.track.kind === "video") {
                        // 接続相手のカメラを描画
                        log(`Add mediastream video track: ${stream.id}`);
                    } else if (event.track.kind === "audio") {
                        log(`Add mediastream audio track: ${stream.id}`);
                        if (remoteAudioRef.current) {
                            remoteAudioRef.current.style.border = "1px solid red";
                            remoteAudioRef.current.autoplay = true;
                            remoteAudioRef.current.controls = false;
                            remoteAudioRef.current.srcObject = stream;
                        }
                    }
                    if (unityInstanceRef.current !== undefined) {
                        unityInstanceRef.current.SendMessage("GameManager", "WebRTCConnect");
                    }
                });
                sendrecv.on("removetrack", (event) => {
                    log("sora removetrack");
                    if (remoteAudioRef.current) {
                        remoteAudioRef.current.srcObject = null;
                    }
                    if (unityInstanceRef.current !== undefined) {
                        unityInstanceRef.current.SendMessage("GameManager", "WebRTCDisconnect");
                    }
                    sendrecv.removeVideoTrack(stream);
                    sendrecv.removeAudioTrack(stream);
                    sendrecv.disconnect();
                });
                sendrecv.on("datachannel", (event: DataChannelEvent) => {
                    // ここにデータチャネルが利用可能になった際の処理を書く
                    const label = event.datachannel.label;
                    const direction = event.datachannel.direction;
                    // log(`sora datachannel: label = ${label}, direction = ${direction}`);
                });
                sendrecv.on("message", async (event: DataChannelMessageEvent) => {
                    // ここでデータチャネルメッセージイベントを処理する
                    const label = event.label;
                    const data = event.data;
                    // log(`sora message: label = ${label}, data length = ${data.byteLength}`);
                    if (unityInstanceRef.current !== undefined) {
                        const message = new TextDecoder().decode(data);
                        switch (label) {
                            case "#systemCall":
                                // システムコール受信
                                {
                                    log(`received system call: message = ${message}`);
                                    switch (message) {
                                        case "enable camera":
                                            // Webカメラ映像を送信
                                            {
                                                if (rtcSettings?.video_code_type != "non") {
                                                    // カメラデバイスからストリームを取得
                                                    const cameraStream = await navigator.mediaDevices.getUserMedia({
                                                        audio: true,
                                                        video: true,
                                                    });
                                                    sendrecv.replaceVideoTrack(stream, cameraStream.getVideoTracks()[0]);
                                                }
                                            }
                                            break;
                                        case "enable capture":
                                            // アプリ画面を送信
                                            {
                                                if (rtcSettings?.video_code_type != "non") {
                                                    // キャンバスからストリーム取得
                                                    const canvasElement = unityContainerRef.current?.querySelector("canvas");
                                                    if (canvasElement == null || canvasElement == undefined) {
                                                        console.error("canvas element is invalid");
                                                        return;
                                                    }
                                                    const canvasStream = canvasElement.captureStream();
                                                    const canvasVideoTrack = canvasStream.getVideoTracks()[0];
                                                    sendrecv.replaceVideoTrack(stream, canvasVideoTrack);
                                                }
                                            }
                                            break;
                                        default:
                                            // Unity側で処理
                                            unityInstanceRef.current.SendMessage("GameManager", "ReceivedSystemCall", message);
                                            break;
                                    }
                                }
                                break;
                            case "#documentImage":
                                {
                                    // 資料画像ファイル名受信
                                    const fileName = message;
                                    unityInstanceRef.current.SendMessage("GameManager", "ReceivedDocumentFileName", fileName);
                                }
                                break;
                            case "#faceInfo":
                                // 顔情報受信
                                unityInstanceRef.current.SendMessage("GameManager", "ReceivedFaceInfo", message);
                                break;
                            case "#voiceVolume":
                                {
                                    // 通話相手の音声音量受信
                                    const vol = parseFloat(message);
                                    unityInstanceRef.current.SendMessage("GameManager", "ReceivedVoiceVolume", vol);
                                }
                                break;
                            case "#inputText":
                                {
                                    // 音声ファイル名受信
                                    const fileName = message;
                                    unityInstanceRef.current.SendMessage("GameManager", "ReceivedInputTextFileName", fileName);
                                }
                                break;
                            case "#translationText":
                                {
                                    // 翻訳テキスト受信
                                    const translationText = message;
                                    unityInstanceRef.current.SendMessage("GameManager", "ReceivedTranslationText", translationText);
                                }
                                break;
                            default:
                                console.error(`Invalid datachannel message received.`);
                                break;
                        }
                    }
                });
                // Sora接続
                await sendrecv.connect(stream);
                // Sora保持
                setSora(sendrecv);
            } catch (err) {
                console.error("ConnectSora error: " + err);
            }
        };

        const ConnectSkyWay = async (channel: string, accessToken: string) => {
            // SkyWay JavaScript SDKをdynamic import
            const { SkyWayProvider } = await import("../util/SkyWayProvider");

            const OnDisconnected = () => {
                if (remoteAudioRef.current) {
                    remoteAudioRef.current.srcObject = null;
                }
                if (unityInstanceRef.current !== undefined) {
                    unityInstanceRef.current.SendMessage("GameManager", "WebRTCDisconnect");
                }
            };

            // データストリーム受信時の処理
            const OnData = async (data: SkyWayDataStreamMessageType) => {
                if (typeof data !== "string") {
                    log(`OnData: received data is not string.`);
                    return;
                }
                const match = data.match(/^\[(.*?)\](.*)$/);
                const label = match?.[1].toLowerCase() ?? "";
                const message = match?.[2]?.trim() ?? "";
                if (label !== "faceinfo" && label !== "voicevolume") {
                    log(`OnData: skyway data stream message: label = ${label}, message = ${message}`);
                }
                if (unityInstanceRef.current !== undefined) {
                    switch (label) {
                        case "systemcall":
                            // システムコール受信
                            {
                                switch (message) {
                                    case "enable camera":
                                        // Webカメラ映像を送信
                                        {
                                            if (rtcSettings?.video_code_type != "non") {
                                                client.switchToCamera();
                                            }
                                        }
                                        break;
                                    case "enable capture":
                                        // アプリ画面を送信
                                        {
                                            if (rtcSettings?.video_code_type != "non") {
                                                client.switchToCanvas();
                                            }
                                        }
                                        break;
                                    default:
                                        // Unity側で処理
                                        unityInstanceRef.current.SendMessage("GameManager", "ReceivedSystemCall", message);
                                        break;
                                }
                            }
                            break;
                        case "documentimage":
                            {
                                // 資料画像ファイル名受信
                                const fileName = message;
                                unityInstanceRef.current.SendMessage("GameManager", "ReceivedDocumentFileName", fileName);
                            }
                            break;
                        case "faceinfo":
                            // 顔情報受信
                            unityInstanceRef.current.SendMessage("GameManager", "ReceivedFaceInfo", message);
                            break;
                        case "voicevolume":
                            {
                                // 通話相手の音声音量受信
                                const vol = parseFloat(message);
                                unityInstanceRef.current.SendMessage("GameManager", "ReceivedVoiceVolume", vol);
                            }
                            break;
                        case "inputtext":
                            {
                                // 音声ファイル名受信
                                const fileName = message;
                                unityInstanceRef.current.SendMessage("GameManager", "ReceivedInputTextFileName", fileName);
                            }
                            break;
                        case "translationtext":
                            {
                                // 翻訳テキスト受信
                                const translationText = message;
                                unityInstanceRef.current.SendMessage("GameManager", "ReceivedTranslationText", translationText);
                            }
                            break;
                        default:
                            console.error(`Invalid datachannel message received.`);
                            break;
                    }
                }
            };

            // セットアップ
            const publishVideo = rtcSettings?.video_code_type != "non";
            const client = await SkyWayProvider(accessToken, {
                roomName: channel,
                publishAudio: true,
                publishVideo: publishVideo,
                remoteAudioElement: remoteAudioRef.current,
                canvasElement: unityContainerRef.current?.querySelector("canvas"),
                resolutionWidth: rtcSettings?.video_resolution_width,
                resolutionHeight: rtcSettings?.video_resolution_height,
                onData: OnData,
                onDisconnected: OnDisconnected,
            });

            // 接続開始
            await client.join();

            // SkyWay保持
            setSkyWay(client);
        };

        const sendOrientationTypeHandler = (ev: CustomEvent<SetVerticalLayoutFlagType>) => {
            // このタイミングでURLパラメータで指定された画面向きをUnity側に送信する
            if (unityInstanceRef.current !== undefined && orientationParameter != null) {
                unityInstanceRef.current.SendMessage("GameManager", "SetOrientationParameter", orientationParameter);
            }
            // 画面向きURLパラメータ未指定時はユーザ設定値を使用する
            if (orientationParameter == null) {
                verticalFlag = ev.detail.verticalFlag;
            } else {
                verticalFlag = orientationParameter == "portrait";
            }
            // キャンバスサイズ変更
            const canvasElement = unityContainerRef.current?.querySelector("canvas");
            resizeCanvas(canvasElement, verticalFlag);
        };

        const logout = () => {
            if (unityInstanceRef.current !== undefined) {
                unityInstanceRef.current.SendMessage("GameManager", "Logout");
            }
        };

        // ページ終了直前の処理
        const handleBeforeUnload = () => {
            log("beforeunload");
            // sora切断
            WebRTCDisconnect();
            // ログアウト
            logout();
        };

        // ブラウザサイズ変更時コールバック
        const handleResizeWindow = () => {
            const canvasElement = unityContainerRef.current?.querySelector("canvas");
            let isVertical;
            if (orientationParameter == null) {
                // URLパラメータ未指定時はユーザ設定で判断(未指定かつ設定取得前だったらデフォルト=false)
                isVertical = verticalFlag;
            } else if (orientationParameter == "portrait") {
                isVertical = true;
            } else {
                isVertical = false;
            }
            resizeCanvas(canvasElement, isVertical);
        };

        // ソケットイベント追加
        addSocketEvent();
        // ユーザートークン取得イベント購読
        window.addEventListener("send_user_token", receivedTokenHandler as EventListenerOrEventListenerObject);
        // RTC設定取得イベント購読
        window.addEventListener("send_rtc_settings", receivedRtcSettingsHandler as EventListenerOrEventListenerObject);
        // 音声認識設定取得イベント購読
        window.addEventListener("send_voice_recognition_type", receivedVoiceRecognitionTypeHandler as EventListenerOrEventListenerObject);
        // 縦横タイプイベント購読
        window.addEventListener("send_orientationtype", sendOrientationTypeHandler as EventListenerOrEventListenerObject);
        // シグナリングサーバ接続開始イベント購読
        window.addEventListener("signaling_connect", connectHandler);
        // シグナリングサーバ接続終了イベント購読
        window.addEventListener("signaling_disconnect", disconnectHandler);
        // シグナリングサーバログインイベント購読
        window.addEventListener("signaling_login", loginHandler as EventListenerOrEventListenerObject);
        // WebRTC接続切断イベント購読
        window.addEventListener("webrtc_disconnect", WebRTCDisconnect);
        // ページ終了直前イベント購読
        window.addEventListener("beforeunload", handleBeforeUnload);
        // コンポーネント再読み込みイベント購読
        window.addEventListener("reload_unitycomponent", renderComponent);
        // ブラウザリサイズイベント購読
        window.addEventListener("resize", handleResizeWindow);

        return () => {
            // ソケットイベント解除
            removeSocketEvent();
            // ユーザートークン取得イベント購読解除
            window.removeEventListener("send_user_token", receivedTokenHandler as EventListenerOrEventListenerObject);
            // RTC設定取得イベント購読解除
            window.removeEventListener("send_rtc_settings", receivedRtcSettingsHandler as EventListenerOrEventListenerObject);
            // 音声認識設定取得イベント購読解除
            window.removeEventListener("send_voice_recognition_type", receivedVoiceRecognitionTypeHandler as EventListenerOrEventListenerObject);
            // 縦横タイプイベント購読
            window.removeEventListener("send_orientationtype", sendOrientationTypeHandler as EventListenerOrEventListenerObject);
            // シグナリングサーバ接続開始イベント購読解除
            window.removeEventListener("signaling_connect", connectHandler);
            // シグナリングサーバ接続終了イベント購読解除
            window.removeEventListener("signaling_disconnect", disconnectHandler);
            // シグナリングサーバログインイベント購読解除
            window.removeEventListener("signaling_login", loginHandler as EventListenerOrEventListenerObject);
            // WebRTC接続切断イベント購読解除
            window.removeEventListener("webrtc_disconnect", WebRTCDisconnect);
            // ページ終了直前イベント購読解除
            window.removeEventListener("beforeunload", handleBeforeUnload);
            // コンポーネント再読み込みイベント購読解除
            window.removeEventListener("reload_unitycomponent", renderComponent);
            // ブラウザリサイズイベント購読解除
            window.removeEventListener("resize", handleResizeWindow);
        };
    }, [userToken, sora, skyway, rtcSettings, renderCount]);

    // フルスクリーンボタン設定
    const ClickFullscreenBtn = () => {
        unityInstanceRef.current?.SetFullscreen(true);
    };

    const openLicenseFile = async () => {
        const url = process.env.PUBLIC_URL + "/license.txt";
        try {
            const response = await fetch(url);
            const text = await response.text();
            // 別タブで開く
            const newTab = window.open(url, "_blank");
            if (newTab) {
                newTab.document.open();
                // 文字コードをUTF-8に設定
                newTab.document.writeln(`<meta charset="UTF-8">`);
                // license.txt の内容を表示
                newTab.document.writeln(`<pre>${text}</pre>`);
                newTab.document.close();
                /*
                // 文字コードをUTF-8に設定
                const metaCharset = newTab.document.createElement("meta");
                metaCharset.setAttribute("charset", "UTF-8");
                newTab.document.head?.appendChild(metaCharset);
                // license.txt の内容を表示
                const preElement = newTab.document.createElement("pre");
                preElement.textContent = text;
                newTab.document.body?.appendChild(preElement);
                */
            }
        } catch (error) {
            console.error("openLicenseFile fetch error:", error);
        }
    };

    const openHelpPage = async () => {
        try {
            window.open(HelpPageURL, "_blank");
        } catch (error) {
            console.error("openHelpPage fetch error:", error);
        }
    };

    return (
        <>
            <div className={styles.canvas_wrapper}>
                <div className={styles.canvas} ref={unityContainerRef} />
            </div>
            <div className={styles.button_area}>
                <Button
                    className={styles.button}
                    variant="contained"
                    disabled={!fullscreenBtnEnabled}
                    onClick={ClickFullscreenBtn}
                    sx={{ display: fullscreenBtnToggle ? "inherit" : "none", bgcolor: "#4D8E8A", "&:hover": { bgcolor: "#3A827E" } }}
                >
                    フルスクリーン
                </Button>
                <Button
                    className={styles.button}
                    variant="contained"
                    onClick={openLicenseFile}
                    sx={{ display: licenseBtnToggle ? "inherit" : "none", bgcolor: "#4D8E8A", "&:hover": { bgcolor: "#3A827E" } }}
                >
                    ライセンス
                </Button>
                <Button
                    className={styles.button}
                    variant="contained"
                    onClick={openHelpPage}
                    sx={{ display: helpBtnToggle ? "inherit" : "none", bgcolor: "#4D8E8A", "&:hover": { bgcolor: "#3A827E" } }}
                >
                    ヘルプ
                </Button>
                <audio id="audio-element" ref={remoteAudioRef}>
                    Remote Audio
                </audio>
            </div>
        </>
    );
};
